import { MAX_EVENTS_PER_REQUEST } from '@geni/contracts/limits';
import type { ConversationTurn, EventId, Locale, LocaleBundle } from '@geni/contracts';
import { normaliseSport } from '@geni/contracts/sport';
import { normaliseOddsFormat } from '@geni/contracts/odds-format';
import { plausibleAmounts } from '@geni/contracts/money';
import { WidgetApiError, WidgetClient, preferredLocale } from './client.js';
import {
  createNode,
  paint,
  type DomDocument,
  type DomNode,
  type PaintedTree,
} from './element.js';
import { renderLauncher, renderWidget } from './render.js';
import { ACTION_MS, pickTeaser, teaserSportFor } from './teaser.js';
import { createReporter, worthReporting, type Reporter } from './report.js';
import { WIDGET_STYLES } from './styles.js';
import { THEME_TOKENS, themeProperties } from './theme.js';
import { DEFAULT_BRAND, initialState, reduce, type WidgetErrorKind, type WidgetState } from './state.js';
import { toViewModel } from './view-model.js';

/**
 * `<betting-widget>` — the element an operator puts on their page.
 *
 * Everything above this file is a pure function. This is where a state machine,
 * a stylesheet and an HTTP client become one tag someone can paste into a
 * casino's template, and it is deliberately the last place any decision is
 * made: it wires, it does not judge.
 *
 * **Shadow DOM, closed to the host's CSS.** A betting site's stylesheet is not
 * ours to predict — `* { box-sizing: content-box }`, a `button { }` reset, a
 * `z-index` war. Inside a shadow root none of it applies and none of ours
 * escapes. The one thing that deliberately crosses the boundary is the theme:
 * custom properties inherit through a shadow root, which is exactly why the
 * palette is expressed as custom properties.
 *
 * **The DOM is declared, not imported.** As in `element.ts`, adding `"DOM"` to
 * the compiler's `lib` would hand `document` to every server package in the
 * repository. So the browser surface this file needs is written out below and
 * `defineBettingWidget` takes it as an argument. The nice consequence is that
 * the element is testable: a fake window with four methods drives the whole
 * lifecycle, no browser and no jsdom.
 */

/** A node, plus the parts of a real element the binder does not need. */
export interface WidgetHostNode extends DomNode {
  getAttribute(name: string): string | null;
  setAttribute(name: string, value: string): void;
  style: {
    setProperty(property: string, value: string): void;
    removeProperty(property: string): void;
  };
  attachShadow(init: { mode: 'open' | 'closed' }): DomNode & WidgetEventTarget;
  dispatchEvent(event: unknown): boolean;
  addEventListener(type: string, listener: (event: WidgetDomEvent) => void): void;
}

export interface WidgetEventTarget {
  addEventListener(type: string, listener: (event: WidgetDomEvent) => void): void;
  querySelector(selectors: string): WidgetFormNode | null;
  appendChild(child: DomNode): unknown;
}

/** Just enough of an event to route a click and stop a form navigating. */
export interface WidgetDomEvent {
  type: string;
  target: unknown;
  /** Present on keydown. Used only to recognise Enter. */
  key?: string;
  preventDefault(): void;
}

export interface WidgetFormNode extends DomNode {
  value?: string;
  focus?(): void;
  scrollTop?: number;
  scrollHeight?: number;
  closest?(selectors: string): WidgetFormNode | null;
  getAttribute(name: string): string | null;
}

export interface WidgetBrowser {
  /** The base class. Extended below rather than imported for the reason above. */
  HTMLElement: new () => WidgetHostNode;
  customElements: { define(name: string, constructor: unknown): void; get(name: string): unknown };
  document: DomDocument;
  CustomEvent: new (type: string, init?: { detail?: unknown; bubbles?: boolean }) => unknown;
  navigator?: { language?: string };
  fetch?: typeof globalThis.fetch;
  setTimeout?(handler: () => void, timeout: number): unknown;
  /**
   * How the launcher's stage picks its next action.
   *
   * Taken from the browser surface rather than reached for, on exactly the same
   * terms as `document` and `fetch`: a test drives the whole teaser loop with a
   * counter and asserts the sequence, instead of watching a real one and hoping.
   */
  random?(): number;
  /**
   * Just enough of the CSS object model to register one custom property.
   *
   * Optional, and everything works without it — the launcher's name simply
   * stands still. See `registerTurnAngle` for why a stylesheet `@property`
   * rule could not do this on its own.
   */
  CSS?: { registerProperty?(definition: Record<string, unknown>): void };
}

/**
 * Teach the engine that `--geni-turn` holds an angle.
 *
 * Without this the browser treats it as an unregistered custom property, which
 * means a keyframe from `0deg` to `360deg` does not interpolate — it *snaps*,
 * and a snap from zero to a full turn is indistinguishable from nothing
 * happening. That is exactly what shipped: the animation ran, the property
 * changed, and the name never moved.
 *
 * The stylesheet does carry an `@property` rule, and it is not enough on its
 * own: the widget's CSS lives inside a shadow root, and a property declared
 * there does not reach the global registry the way a document stylesheet's
 * does. Registering from script does, and the rule stays as documentation of
 * the shape.
 *
 * Wrapped, because registering the same name twice throws — two copies of this
 * bundle on one page must not take it down — and because a browser without the
 * API at all should lose the flourish rather than the widget.
 */
function registerTurnAngle(browser: WidgetBrowser): void {
  try {
    browser.CSS?.registerProperty?.({
      name: '--geni-turn',
      syntax: '<angle>',
      initialValue: '0deg',
      inherits: true,
    });
  } catch {
    // Already registered, or not supported. Neither is worth a broken widget.
  }
}

export const WIDGET_TAG = 'betting-widget';

/** Attributes that are configuration rather than theme. */
const CONFIG_ATTRIBUTES = [
  'api-base',
  'publishable-key',
  /**
   * The demo link the page was opened from, e.g. `demo-link="a7f3k2"`.
   *
   * Only a demo page ever sets it. See WidgetClientOptions.
   */
  'demo-link',
  'locale',
  'event-ids',
  'theme',
  'launcher-label',
  /**
   * The operator's own place-bet endpoint, e.g. `/api/v1/bets/place`.
   *
   * Must be same-origin with the page: the bettor is identified by the
   * operator's session cookie, and a cross-origin URL does not get one. Unset
   * turns in-chat placement off entirely and restores the handoff — a
   * confirmed bet fires `geni:confirmed` with the signed ticket and the
   * operator's page takes it from there, exactly as before this existed.
   */
  'place-bet-url',
  /**
   * The operator's own balance endpoint, e.g. `/api/v1/accounts/me`.
   *
   * Same rule as the place-bet URL and for the same reason: it must be
   * same-origin, because the bettor is identified by the operator's session
   * cookie and a cross-origin request does not carry one. Unset means no
   * account, which turns the affordability check and the quick stake amounts
   * off — the behaviour the widget had before this existed, and a legitimate
   * way to run it.
   *
   * Our own API is deliberately not asked for this. A session here is minted
   * from a publishable key and carries no identity, so our server does not
   * know whose balance to fetch and should not be told.
   */
  'account-url',
  /**
   * Where the widget reports its own failures, e.g. `report-url="/geni/errors"`.
   *
   * Unset means no reporting at all, which is what every deployment has today
   * and exactly how the widget behaved before this existed. It is opt-in rather
   * than on by default because this code runs inside somebody else's page: an
   * operator should be told it is happening, and the place they are told is
   * the attribute they typed.
   *
   * What it closes is the one fault our diagnosis screen cannot see. Every
   * slice there is built from interaction records, and a record exists only
   * because a request reached us — so a widget that throws while rendering
   * gives the bettor a dead panel and tells nobody. It is also the only class
   * of fault that cannot appear before the first real page, which makes it the
   * likeliest thing to appear on it.
   *
   * Nothing a bettor typed is ever sent. See `report.ts`.
   */
  'report-url',
  /**
   * Which sport the visitor is looking at, in the operator's own words.
   *
   * "CS:GO", "Counter-Strike", "soccer_epl" and "Fútbol" all work —
   * `normaliseSport` reads them, because an operator who has to look up our
   * spelling to make a feature work will not use the feature. Unset behaves
   * exactly as the widget did before sport context existed.
   *
   * Declared by the page rather than sniffed out of it, and that is a decision
   * rather than a shortcut: a widget that scraped its host's DOM for a heading
   * would break on the first redesign, and it would be reaching across the
   * shadow boundary the rest of this file exists to respect. The page already
   * tells us which fixtures are on screen; this is the same conversation.
   */
  'sport',
  /**
   * How the page shows prices: `decimal`, `american`, `fractional`,
   * `hongkong`, `indonesian` or `malaysian`.
   *
   * Set it from the page's own "odds format" menu and change it when the
   * bettor does — the panel repaints on the spot, like it does for `sport`.
   * "European", "US" and "UK" work too. Unset, or anything unrecognised,
   * shows decimal, which is what the panel always showed.
   *
   * Declared by the page for the same reason as the sport: the choice is
   * usually stored somewhere we cannot and should not read, often inside a
   * sportsbook frame from another origin.
   */
  'odds-format',
  /**
   * Openers the operator wants offered, newline- or pipe-separated.
   *
   * Wins over the written per-sport fallback, because only the operator can
   * name a match that is on the board right now — the widget is handed event
   * ids, never team names.
   */
  'starter-prompts',
] as const;


/**
 * Events the host page can listen for.
 *
 * `geni:confirmed` carries the signed ticket and is the integration point for
 * the handoff flow: the operator's page takes that string to their betting
 * engine. `geni:placed` is its in-chat counterpart, fired after the operator's
 * own endpoint has accepted a bet.
 *
 * Nothing in this file places a bet either way. It confirms a price with us and
 * hands a signed ticket plus an amount to the operator, whose engine debits
 * their own ledger. No money passes through here, which is the same line the
 * product has always drawn.
 */
export const WIDGET_EVENTS = {
  opened: 'geni:opened',
  closed: 'geni:closed',
  confirmed: 'geni:confirmed',
  /** A bet the operator's own engine accepted. Carries their reference. */
  placed: 'geni:placed',
  /**
   * The bettor asked to add funds.
   *
   * An event, never a navigation. Where a deposit flow lives — a modal, a
   * route, a different subdomain — is the operator's business, and a widget
   * that navigated the page away would take a bettor out of a conversation
   * they are in the middle of. It only ever fires where the operator has a
   * flow AND the jurisdiction permits offering one.
   */
  topUp: 'geni:topup',
  failed: 'geni:failed',
} as const;

/**
 * How much of the conversation travels with a parse, and how long a turn may be.
 *
 * Duplicated from `MAX_HISTORY_TURNS` / `MAX_HISTORY_TURN_CHARS` in contracts
 * rather than imported, and the duplication is the boundary rather than
 * laziness: this file ships to a betting page, so it may import shapes as
 * TYPES only — a value import from `@geni/contracts` drags Zod into the
 * bundle, which is the mistake `money.ts` documents at length. A test pins
 * these two numbers to the contract's, which is the same arrangement the
 * target-odds bounds already live under in `packages/nlu`.
 */
export const MAX_HISTORY_TURNS = 6;
export const MAX_HISTORY_TURN_CHARS = 2000;

/**
 * The last few turns, in the wire's vocabulary.
 *
 * Trimmed here rather than server-side because the server rejects an oversized
 * history instead of silently cutting it — a 400 in the middle of a
 * conversation is worse than a shorter memory, and the widget is the only party
 * that knows which end of the transcript matters.
 */
export function recentTurns(
  messages: readonly { from: string; text: string }[],
): ConversationTurn[] {
  return messages.slice(-MAX_HISTORY_TURNS).map((message) => ({
    role: message.from === 'player' ? ('bettor' as const) : ('assistant' as const),
    text: message.text.slice(0, MAX_HISTORY_TURN_CHARS),
  }));
}

/** A clock time for a message bubble, in the bettor's own format. */
function clockTime(lang: string, at: Date): string {
  try {
    return new Intl.DateTimeFormat(lang, { hour: '2-digit', minute: '2-digit' }).format(at);
  } catch {
    // Same reasoning as formatPrice: an odd tag must not take the panel down.
    return '';
  }
}

function errorKind(error: unknown): WidgetErrorKind {
  return error instanceof WidgetApiError ? error.kind : 'GENERIC';
}

/**
 * A typed amount, read carefully.
 *
 * The hard part is not punctuation, it is that "1,000" means one thousand to an
 * English bettor and one to a Spanish, Portuguese, Turkish or Russian one — and
 * this widget ships in all five languages. Guessing wrong in the generous
 * direction stakes a thousand times what somebody meant.
 *
 * So separators are resolved where they can be, and the input is REFUSED where
 * they cannot:
 *
 *   "20"       -> 20        one number, no ambiguity
 *   "20,50"    -> 20.5      one separator, two trailing digits: a decimal
 *   "1.234,50" -> 1234.5    both kinds present, so the last one is the decimal
 *   "1,000"    -> null      three trailing digits, and the two readings are a
 *                           factor of a thousand apart
 *
 * A refusal clears the stake, which puts the assistant's question back on
 * screen. That is a worse outcome than understanding them and a far better one
 * than placing a bet a thousand times the size they meant. The suggested
 * amounts beside the field are always unambiguous, so there is a way through
 * that needs no typing at all.
 *
 * (Currencies with three decimal places — KWD, BHD — lose "1,000" meaning one
 * unit. Neither is in a shipping locale, and a suggestion chip still works.)
 */
export function parseStake(raw: string | undefined): number | null {
  if (raw === undefined) return null;

  // One implementation of "what number is this", shared with the server-side
  // corroborator via the Zod-free money subpath. Two parsers for the same
  // question is how the field and the check that validates it come to disagree
  // about what a bettor typed.
  const readings = plausibleAmounts(raw);

  // A field has to resolve to one amount, so an ambiguous input is refused
  // rather than guessed — "1,000" could be a thousand or it could be one, and
  // silently picking is how somebody stakes the wrong figure. `amountsWrittenIn`
  // takes the other branch and accepts either, because its question is only
  // whether the bettor wrote the number at all.
  return readings.length === 1 ? (readings[0] ?? null) : null;
}

export function defineBettingWidget(browser: WidgetBrowser): void {
  // Defining twice throws a DOMException and would take down a page that
  // happened to include the bundle from two places.
  if (browser.customElements.get(WIDGET_TAG) !== undefined) return;

  registerTurnAngle(browser);

  class BettingWidget extends browser.HTMLElement {
    private state: WidgetState = initialState;
    /**
     * Which teaser loop is live.
     *
     * Bumped whenever the loop should stop — the panel opening, the element
     * going away, the sport changing. A pending timer checks it and returns,
     * which is why no `clearTimeout` is needed and why the browser surface did
     * not have to grow one. A stale callback cannot schedule its successor, so
     * the chain ends rather than leaking.
     */
    private teaserRun = 0;
    /**
     * Whether the panel on screen is one that arrived this opening.
     *
     * Held for as long as the panel is open, not spent on the first paint. The
     * binder patches nodes in place now, so a paint that dropped the marker
     * would take the class off the *same* element the entrance is playing on
     * and cut the animation short — the opposite of the flash it was added to
     * stop. The panel node itself is what makes this correct: closing removes
     * it from the tree, so reopening builds a fresh one and the animation runs
     * again because the node is new, not because the class was re-added.
     *
     * Deliberately a field here rather than a flag in `WidgetState`: clearing
     * it has to happen without a repaint, and anything in the state cannot be
     * changed without one. See the `entering` argument to `renderWidget`.
     */
    private panelEntering = false;
    /** The last tree actually painted, as JSON. See repaint(). */
    private lastPainted: string | null = null;
    /** What the last paint left in the DOM, so the next one can diff it. */
    private painted: PaintedTree | null = null;
    private root: (DomNode & WidgetEventTarget) | null = null;
    private client: WidgetClient | null = null;
    private locale: Locale = 'en' as Locale;
    /** Set when the next paint should put the cursor back in the composer. */
    private focusInput = false;

    /** The same, for the field that "add another match" turns into. */
    private focusBuild = false;
    /** How many messages there were when the transcript was last pinned. */
    private scrolledAt = 0;
    /** Scroll offset captured before the tree was replaced. */
    private scrollWas = 0;

    static get observedAttributes(): string[] {
      return [...CONFIG_ATTRIBUTES, ...THEME_TOKENS.map((token) => token.attribute)];
    }

    connectedCallback(): void {
      if (this.root !== null) return;

      const root = this.attachShadow({ mode: 'open' });
      this.root = root;

      // The stylesheet is a constant assigned as textContent, on exactly the
      // same terms as everything else here: nothing is parsed as markup.
      const style = browser.document.createElement('style');
      style.textContent = WIDGET_STYLES;
      root.appendChild(style);

      this.applyTheme();
      this.applyContext();
      this.startTeaser();

      // One listener per event type on the shadow root rather than one per
      // button. The tree is rebuilt on every state change, so a handler bound
      // to a node would be bound to a node that no longer exists.
      root.addEventListener('click', (event) => { this.onClick(event); });
      root.addEventListener('submit', (event) => { this.onSubmit(event); });
      root.addEventListener('keydown', (event) => { this.onKeydown(event); });
      // `change` rather than `input`, and that is the whole reason the stake
      // field works. Every state change rebuilds the tree, so reading on each
      // keystroke would replace the box the bettor is typing into, mid-digit.
      // `change` fires once they are done — on blur, or on Enter.
      root.addEventListener('change', (event) => { this.onChange(event); });
      root.addEventListener('input', (event) => { this.onStakeTyping(event); });

      this.repaint();
    }

    attributeChangedCallback(): void {
      // Theme first: an operator flipping `accent-color` at runtime should not
      // need the widget to re-render to see it, and a repaint before the
      // properties are set would flash the old palette.
      this.applyTheme();
      this.applyContext();
      // The sport may have changed, and the stage on screen belongs to the old
      // one. Restarting rather than letting the current action finish, because
      // a football being kicked on the CS:GO page is the exact confusion the
      // sport attribute exists to prevent.
      this.startTeaser();
      if (this.root !== null) this.repaint();
    }

    /**
     * Drive the collapsed pill's mini-stage.
     *
     * One action plays, then the character rests for two to three seconds, then
     * a different action is picked. The rest is the point: a stage that fires
     * every half second reads as a spinner and stops being looked at.
     *
     * It runs only while the panel is closed, and only while a sport is
     * declared. Opening the panel ends the loop — the launcher is not on screen,
     * and repainting the whole widget on a timer underneath a bettor who is
     * typing would be a cost paid for nothing.
     */
    private startTeaser(): void {
      this.teaserRun += 1;
      const run = this.teaserRun;

      const sport = teaserSportFor(this.state.context.sport);
      if (sport === null) {
        if (this.state.teaser !== null) this.update(reduce(this.state, { type: 'TEASER', teaser: null }));
        return;
      }

      const schedule = (delay: number): void => {
        // No timer available — a test stub, or a host without one. The stage
        // draws its first action and stays there, which is a still picture
        // rather than a broken one.
        browser.setTimeout?.(() => {
          if (run !== this.teaserRun || this.state.phase !== 'CLOSED') return;

          const next = pickTeaser(sport, browser.random ?? Math.random, this.state.teaser);
          this.update(reduce(this.state, { type: 'TEASER', teaser: next }));
          schedule(ACTION_MS + next.pauseMs);
        }, delay);
      };

      const first = pickTeaser(sport, browser.random ?? Math.random, this.state.teaser);
      this.state = reduce(this.state, { type: 'TEASER', teaser: first });
      if (this.root !== null) this.repaint();
      schedule(ACTION_MS + first.pauseMs);
    }

    private applyTheme(): void {
      const supplied = themeProperties((name) => this.getAttribute(name));

      for (const token of THEME_TOKENS) {
        const value = supplied.get(token.property);

        // Removed, not skipped. An operator clearing `accent-color` — or
        // mistyping it, which sanitising turns into the same thing — must fall
        // back to the stylesheet, and an inline property left behind from the
        // previous value outranks every rule there.
        if (value === undefined) this.style.removeProperty(token.property);
        // setProperty, never string concatenation into the stylesheet. See
        // theme.ts — this is what makes an operator-supplied value a value.
        else this.style.setProperty(token.property, value);
      }
    }

    private ensureClient(): WidgetClient {
      if (this.client !== null) return this.client;

      this.client = new WidgetClient({
        baseUrl: this.getAttribute('api-base') ?? '',
        publishableKey: this.getAttribute('publishable-key') ?? '',
        ...(this.getAttribute('demo-link')?.trim()
          ? { demoLink: this.getAttribute('demo-link')!.trim() }
          : {}),
        ...(browser.fetch === undefined ? {} : { fetch: browser.fetch }),
      });
      return this.client;
    }

    /**
     * The page context, read off the attributes and pushed into state.
     *
     * Called on every attribute change as well as at connect, so an operator
     * switching sport tabs without remounting the element gets new openers
     * immediately — which is exactly what a single-page sportsbook does.
     */
    private applyContext(): void {
      const raw = this.getAttribute('starter-prompts') ?? '';
      const prompts = raw
        .split(/[|\n]/)
        .map((prompt) => prompt.trim())
        .filter((prompt) => prompt !== '');

      this.state = reduce(this.state, {
        type: 'CONTEXT',
        sport: normaliseSport(this.getAttribute('sport')),
        oddsFormat: normaliseOddsFormat(this.getAttribute('odds-format')),
        prompts,
        // The operator's own name for the assistant. One source for both render
        // paths, so the pill cannot say one thing before the bundle arrives and
        // another after.
        brand: this.getAttribute('launcher-label') ?? DEFAULT_BRAND,
      });
    }

    private eventIds(): EventId[] {
      // The operator's page says which fixtures are on screen. An empty list is
      // rejected by the API rather than silently meaning "all of them".
      const named = (this.getAttribute('event-ids') ?? '')
        .split(',')
        .map((id) => id.trim())
        .filter((id) => id !== '') as EventId[];

      /**
       * Capped, because the API caps it and a 400 reaches the bettor as a bug.
       *
       * An operator listing more fixtures than the request allows is making an
       * ordinary mistake — a page that grows its board, a template that prints
       * every match rather than the visible ones — and until this line it was
       * punished with `INVALID_REQUEST` on every message. From inside the panel
       * that is "Something went wrong. Try again.", forever, with nothing on
       * screen suggesting the page is what is wrong.
       *
       * Found exactly that way, and by a change of ours: the demo board moved
       * from six generated fixtures to a live supplier's two hundred and thirty,
       * and every parse in the demo started failing. The widget had been
       * handing the API something it was always going to refuse.
       *
       * Taking the first is right rather than merely easy. The attribute is
       * written in the order the page draws — soonest first on every sportsbook
       * — so the head of the list is what the bettor can actually see, and
       * retrieval widens by name anyway for anything they mention that is not
       * in it.
       */
      return named.slice(0, MAX_EVENTS_PER_REQUEST);
    }

    private dispatch(name: string, detail?: unknown): void {
      this.dispatchEvent(new browser.CustomEvent(name, { detail, bubbles: true }));
    }

    private update(next: WidgetState): void {
      this.state = next;
      this.repaint();
    }

    private repaint(): void {
      const root = this.root;
      if (root === null) return;

      /**
       * Where the reader was, in case the paint moves them.
       *
       * The binder patches in place, so the ordinary repaint leaves the scroll
       * container alone and this is never needed. It still is when a paint
       * changes the *shape* of something above the transcript, because that
       * subtree is rebuilt and a container that leaves the document and comes
       * back returns at zero. Captured here and put back in `afterPaint`,
       * unless a new message arrived — that is the one case where moving the
       * reader is the point.
       */
      this.scrollWas = root.querySelector('.geni-scroll')?.scrollTop ?? 0;

      const vm = toViewModel(this.state);

      // The style element is the shadow root's first child and paint replaces
      // everything, so the tree goes in a container of its own.
      const host = this.container(root);

      // Before the bundle arrives there is no language to render *the panel*
      // in, so only the launcher is drawn — and it needs no translated string.
      // See renderLauncher for why this is not the "flash English" case the
      // null view model exists to prevent.
      const entering = vm !== null && vm.open && this.panelEntering;
      // Rendered WITHOUT the entrance marker, always, so the comparison below
      // is about content and nothing else.
      const spec =
        vm === null
          ? renderLauncher(
              this.state.brand,
              // The stage does not wait for the bundle. It carries no words, so
              // there is nothing to render in the wrong language — which is the
              // only thing the null view model exists to prevent.
              this.state.teaser,
              // No hint yet: there is no language to write one in.
            )
          : renderWidget(vm);

      /**
       * A paint that would draw exactly what is already on screen is skipped.
       *
       * Reopening produces two paints, one for the state change and one when
       * the session settles, and the second draws *byte-identical* output. The
       * binder would now walk the whole tree to discover that and write
       * nothing; this notices in one string comparison and does not walk it.
       *
       * Compared as JSON because that is exactly what an `ElementSpec` is: tags,
       * text, class names and plain attributes, with no functions and no
       * cycles. The tree is a few hundred small nodes, so this costs far less
       * than the walk it avoids.
       *
       * It is no longer what keeps the panel from flickering — the binder is,
       * by reusing nodes rather than replacing them. This is now only a saving.
       */
      const drawn = JSON.stringify(spec);
      if (drawn === this.lastPainted) {
        this.afterPaint(root);
        return;
      }
      this.lastPainted = drawn;

      // Added after the comparison, never before it. See above.
      if (entering) spec.className = `${spec.className ?? ''} geni-panel--entering`.trim();

      this.painted = paint(host, spec, browser.document, this.painted);

      this.afterPaint(root);
    }

    private containerNode: DomNode | null = null;

    private container(root: DomNode & WidgetEventTarget): DomNode {
      if (this.containerNode !== null) return this.containerNode;

      const node = browser.document.createElement('div');
      root.appendChild(node);
      this.containerNode = node;
      return node;
    }

    private afterPaint(root: DomNode & WidgetEventTarget): void {
      // A repaint rebuilds the composer, so the cursor has to be put back or
      // the bettor is typing into a box that vanished mid-sentence.
      if (this.focusInput) {
        this.focusInput = false;
        root.querySelector('.geni-input')?.focus?.();
      }

      // Opened by a press, so the cursor belongs in it: the press was the
      // bettor saying they intend to type.
      if (this.focusBuild) {
        this.focusBuild = false;
        root.querySelector('.geni-build-new')?.focus?.();
      }

      /**
       * Pin the transcript to the newest message — when there is one.
       *
       * Every repaint used to scroll to the bottom, which is right after an
       * answer arrives and wrong every other time. Opening the quick options
       * threw the reader to the foot of the panel, and it read as the whole
       * thing reloading rather than as a panel opening. Anything that is not a
       * new message — ticking an option, opening a disclosure, a price moving —
       * leaves the view where the bettor put it.
       *
       * Done after paint because the height it scrolls to does not exist until
       * the nodes do.
       */
      const scroll = root.querySelector('.geni-scroll');
      if (scroll === null) return;

      const grew = this.state.messages.length > this.scrolledAt;
      this.scrolledAt = this.state.messages.length;

      if (grew && scroll.scrollHeight !== undefined) {
        scroll.scrollTop = scroll.scrollHeight;
        return;
      }

      // Not a new message, so put the reader back exactly where the replaced
      // tree left them.
      scroll.scrollTop = this.scrollWas;
    }

    private onClick(event: WidgetDomEvent): void {
      const target = event.target as WidgetFormNode | null;
      if (target === null || target.closest === undefined) return;

      if (target.closest('.geni-launcher') !== null) { void this.open(); return; }
      if (target.closest('.geni-close') !== null) { this.close(); return; }

      // An opener. Sent as the bettor's own message, because that is what it
      // is — the chip is a shortcut for typing, not a separate kind of request.
      const starter = target.closest('.geni-starter');
      if (starter !== null) {
        const prompt = starter.getAttribute('data-prompt');
        if (prompt !== null && prompt !== '') void this.ask(prompt);
        return;
      }

      /**
       * A verdict on the answer. Applied to the screen at once and sent after.
       *
       * Never awaited and never surfaced on failure: the tap is an opinion,
       * not a transaction. Making somebody watch a spinner to learn whether
       * their opinion saved would cost more than the opinion is worth, and an
       * error about it is not something they could act on.
       */
      const vote = target.closest('.geni-feedback-vote');
      if (vote !== null) {
        const verdict = vote.getAttribute('data-verdict');
        if (verdict !== 'HELPFUL' && verdict !== 'NOT_HELPFUL') return;

        this.update(reduce(this.state, { type: 'VERDICT', verdict }));
        // Approval is the whole message; disapproval waits for a reason, and
        // sending now would store two records for one opinion.
        if (verdict === 'HELPFUL') this.sendFeedback({ verdict });
        return;
      }

      const reason = target.closest('.geni-feedback-reason');
      if (reason !== null) {
        const id = reason.getAttribute('data-feedback-reason');
        if (id !== 'WRONG_MATCH' && id !== 'WRONG_BET' && id !== 'MISUNDERSTOOD') return;

        this.update(reduce(this.state, { type: 'VERDICT_REASON' }));
        this.sendFeedback({ verdict: 'NOT_HELPFUL', reason: id });
        return;
      }

      if (target.closest('.geni-feedback-open') !== null) {
        this.update(reduce(this.state, { type: 'FEEDBACK_FORM', open: true }));
        return;
      }

      // The same button both ways: its label and aria-expanded already say
      // which, so a second control would be one more thing to keep in step.
      if (target.closest('.geni-rg-open') !== null) {
        this.update(reduce(this.state, { type: 'RG_PANEL', open: !this.state.rgOpen }));
        return;
      }

      if (target.closest('.geni-feedback-cancel') !== null) {
        this.update(reduce(this.state, { type: 'FEEDBACK_FORM', open: false }));
        return;
      }

      if (target.closest('.geni-feedback-send') !== null) {
        const box = this.root?.querySelector?.('.geni-feedback-text');
        const written = (box?.value ?? '').trim();
        this.update(reduce(this.state, { type: 'FEEDBACK_FORM', open: false }));
        if (written !== '') this.sendFeedback({ verdict: 'MESSAGE', text: written });
        return;
      }

      const chip = target.closest('.geni-chip');
      if (chip !== null) {
        if (chip.getAttribute('data-stake-action') === 'top-up') {
          this.dispatch(WIDGET_EVENTS.topUp);
          return;
        }
        const amount = Number(chip.getAttribute('data-stake-amount'));
        if (Number.isFinite(amount) && amount > 0) {
          this.update(reduce(this.state, { type: 'STAKE_CHOSEN', amount }));
        }
        return;
      }

      // The in-chat card. Confirms the price with us, then places with the
      // operator — see `place`.
      const place = target.closest('.geni-place');
      if (place !== null) {
        const slipId = place.getAttribute('data-slip-id');
        if (slipId !== null) void this.place(slipId);
        return;
      }

      /**
       * One of several matches, picked from the chip row.
       *
       * Narrows the conversation to that slip and nothing else — no network, no
       * model call, no money. The full card then draws itself for the one bet
       * under consideration, with the button that actually commits it. See
       * `renderChoices`.
       */
      /**
       * One more bet on the slip in hand, pressed rather than described.
       *
       * No model call: the ref was drawn by us and the server checks it against
       * the list it stored beside this slip. A refusal is not an error — the
       * slip stays exactly as it was and a line explains why.
       */
      // Opening and closing the quick options. Local, and cheap enough that it
      // is the only thing this button does.
      if (target.closest('.geni-build-toggle') !== null) {
        this.update(reduce(this.state, { type: 'BUILD_TOGGLED' }));
        return;
      }

      /**
       * Ticking one. Nothing leaves the browser until the confirm.
       *
       * That is what lets somebody change their mind three times without
       * repricing three times, and without a total moving under them while
       * they are still deciding.
       */
      const build = target.closest('.geni-build-chip');
      if (build !== null) {
        const ref = build.getAttribute('data-ref');
        if (ref === null) return;
        this.update(reduce(this.state, { type: 'BUILD_PICKED', ref }));
        return;
      }

      // A leg taken back off the ticket. See the reduce route.
      const minus = target.closest('.geni-card-leg-remove');
      if (minus !== null) {
        const slipId = minus.getAttribute('data-slip-id');
        const selectionId = minus.getAttribute('data-selection-id');
        if (slipId === null || selectionId === null) return;
        void this.reduce(slipId, [selectionId]);
        return;
      }

      /**
       * "Add another match" becomes the field it is offering.
       *
       * It used to sit under a field that was always on screen, so pressing it
       * moved the cursor a centimetre and nothing else — the label promised a
       * thing to do and delivered a focus change. Now the button is where the
       * box will be, and pressing it puts the box there, with the cursor in it.
       */
      /**
       * The arrow sends the field beside it, exactly as Enter in it does.
       *
       * Beside it, not the first on the panel: there are two rows now — one
       * for another market on this fixture, one for a fixture that is not on
       * the ticket yet — and a handler that reached for `.geni-build-field`
       * would have the second arrow sending the first box.
       */
      const send = target.closest('.geni-build-send');
      if (send !== null) {
        const field = send.getAttribute('data-field');
        this.submitBuildOn(field === null ? null : (this.root?.querySelector(`.${field}`) ?? null));
        return;
      }

      if (target.closest('.geni-build-add-button') !== null) {
        this.focusBuild = true;
        this.update(reduce(this.state, { type: 'BUILD_TYPING', open: true }));
        return;
      }

      // Everything ticked, priced once.
      if (target.closest('.geni-build-confirm') !== null) {
        const slipId = this.state.slips[0]?.id;
        if (slipId === undefined || this.state.buildPicked.length === 0) return;
        void this.extend(slipId, [...this.state.buildPicked]);
        return;
      }

      const choice = target.closest('.geni-choice');
      if (choice !== null) {
        const slipId = choice.getAttribute('data-slip-id');
        if (slipId === null) return;
        // What they pressed, for the transcript. See SLIP_CHOSEN.
        const label = choice.getAttribute('data-choice-label')?.trim();
        this.update(
          reduce(this.state, {
            type: 'SLIP_CHOSEN',
            slipId,
            ...(label === undefined || label === null || label === '' ? {} : { label }),
            at: clockTime(this.state.bundle?.resolved ?? this.locale, new Date()),
          }),
        );
        this.focusInput = true;
        return;
      }

      const confirm = target.closest('.geni-confirm');
      if (confirm !== null) {
        const slipId = confirm.getAttribute('data-slip-id');
        if (slipId === null) return;

        /**
         * With several on offer, this button means "this one", not "done".
         *
         * The bettor has picked out of a list; the amount has not been named
         * and cannot have been, because until this press there was nothing to
         * attach it to. Confirming here would sign a slip and hand them to the
         * operator's own bet slip to choose all over again — which is what it
         * did, and what made a price search feel like a different product from
         * naming a bet.
         *
         * Only where an amount may actually be collected. Without that the
         * older road is the only road, and pressing this still takes it.
         */
        if (this.state.slips.length > 1 && this.state.stake !== null) {
          this.update(reduce(this.state, { type: 'SLIP_CHOSEN', slipId }));
          this.focusInput = true;
          return;
        }

        void this.confirm(slipId);
      }
    }

    /**
     * An amount typed into the stake field.
     *
     * Parsed permissively — a bettor writing "£20", "20," or " 20 " means
     * twenty, and refusing them over punctuation would be the widget being
     * pedantic about the one input that matters. Anything that is not a number
     * clears the stake rather than guessing, which puts the assistant's own
     * question back on screen.
     */
    private onChange(event: WidgetDomEvent): void {
      const target = event.target as WidgetFormNode | null;
      if ((target?.closest?.('.geni-stake-input') ?? null) === null) return;

      this.update(reduce(this.state, { type: 'STAKE_CHOSEN', amount: parseStake(target?.value) }));
    }

    /**
     * The payout while they are still typing, not after they leave the field.
     *
     * `change` fires on blur, so a bettor who typed an amount and looked for
     * something to press found no returns line and no Confirm button — and
     * nothing on screen said the field had to be left first. The quick amounts
     * hid it: they work instantly, so only somebody wanting a figure of their
     * own ever met it. Found by typing into the field and waiting.
     *
     * **Only when what they typed is exactly what we would draw.** The field's
     * value is rendered from the parsed number, so updating on every keystroke
     * would rewrite "10." to "10" under the cursor and refuse "1," halfway
     * through — which is why `change` was the right event for this field and
     * still is for those. Round-tripping the string keeps the live update to
     * the cases where the repaint changes nothing the bettor can see.
     *
     * An emptied field clears the stake, so deleting the amount takes the
     * payout away rather than leaving a figure nobody typed.
     */
    private onStakeTyping(event: WidgetDomEvent): void {
      const target = event.target as WidgetFormNode | null;
      if ((target?.closest?.('.geni-stake-input') ?? null) === null) return;

      const raw = (target?.value ?? '').trim();
      if (raw === '') {
        if (this.state.stakeAmount !== null) {
          this.update(reduce(this.state, { type: 'STAKE_CHOSEN', amount: null }));
        }
        return;
      }

      const parsed = parseStake(raw);
      if (parsed === null || String(parsed) !== raw) return;
      if (this.state.stakeAmount === parsed) return;

      this.update(reduce(this.state, { type: 'STAKE_CHOSEN', amount: parsed }));
    }

    private onSubmit(event: WidgetDomEvent): void {
      // Without this the form navigates the operator's whole page away.
      event.preventDefault();
      this.submitComposer();
    }

    /**
     * Enter, handled explicitly rather than left to implicit submission.
     *
     * Pressing Enter in a text field is supposed to submit its form, but that
     * path runs through form association, which a shadow root complicates: the
     * form here is not associated with the host page's document, and how much
     * of the implicit-submission algorithm survives that is not something worth
     * a widget's behaviour depending on. Wiring the key directly costs six
     * lines and removes the question.
     *
     * Both routes can fire without sending twice: `submitComposer` clears the
     * field before it dispatches, so whichever arrives second finds nothing
     * left to send.
     */
    private onKeydown(event: WidgetDomEvent): void {
      const target = event.target as WidgetFormNode | null;
      if (target?.closest === undefined) return;

      /**
       * Escape in the field puts the button back.
       *
       * A control that opens and cannot be closed is a control that has taken
       * the row over. Only in this field, and only when it is empty: Escape
       * over a half-typed sentence would throw the sentence away, and the
       * widget's own Escape is not otherwise ours to intercept — the operator's
       * page may be listening for it.
       */
      if (event.key === 'Escape' && target.closest('.geni-build-new') !== null) {
        if ((target.value ?? '').trim() !== '') return;
        this.update(reduce(this.state, { type: 'BUILD_TYPING', open: false }));
        return;
      }

      if (event.key !== 'Enter') return;

      /**
       * The field under a slip sends the same way, and says what it is for.
       *
       * It is the way out for a bet the ten quick options did not think of, so
       * it goes down the ordinary parse path — a sentence, a model, the gate —
       * rather than the ref-only route the chips use. What makes it *add*
       * rather than replace is `addTo`, which names the slip on screen.
       */
      const typed = target.closest('.geni-build-field');
      if (typed !== null) {
        event.preventDefault();
        this.submitBuildOn(typed);
        return;
      }

      if (target.closest('.geni-input') === null) return;

      event.preventDefault();
      this.submitComposer();
    }

    /**
     * The language actually resolved, not the one asked for — the same
     * distinction the panel's `lang` attribute makes.
     *
     * Every request that names a bet back to the bettor needs it, and each one
     * that forgets it names that bet in somebody else's language.
     */
    private readingLocale(): string | undefined {
      return this.state.bundle?.resolved ?? this.locale;
    }

    private submitComposer(): void {
      const input = this.root?.querySelector('.geni-input') ?? null;
      const text = (input?.value ?? '').trim();
      if (text === '') return;

      // Cleared before the request, not after: the bettor has sent it, and a
      // field that still holds the sentence invites sending it twice.
      if (input !== null) input.value = '';

      void this.ask(text);
    }

    private submitBuildOn(field: WidgetFormNode | null): void {
      const text = (field?.value ?? '').trim();
      if (text === '') return;

      /**
       * Without a slip it is an ordinary question, not a refusal.
       *
       * The field is only drawn under one, so this is close to unreachable —
       * but a slip that expired between the paint and the keystroke would
       * otherwise swallow the sentence, and answering it fresh is what the
       * bettor wanted either way.
       */
      const slipId = this.state.slips[0]?.id;

      // Cleared before the request, not after: the bettor has sent it, and a
      // field that still holds the sentence invites sending it twice.
      if (field !== null) field.value = '';

      void this.ask(text, slipId);
    }

    /**
     * Ask the operator what the bettor holds, and hold nothing ourselves.
     *
     * Called when the panel opens and again after a bet is placed, because a
     * placement moves the number and the next slip should be assessed against
     * what is actually left rather than what was there a minute ago.
     *
     * Never throws and never surfaces a failure. No endpoint, an endpoint that
     * is down, a visitor who is not logged in — all land on null, which means
     * no account, which is the state the widget shipped in for a year.
     */
    /**
     * The widget's own reporter, or nothing.
     *
     * Built once and cached, including the "no reporter" answer: an operator
     * who has not set `report-url` gets exactly the widget that shipped before
     * this existed, and asking the DOM for a missing attribute on every swallow
     * would be a cost paid by everybody for a feature nobody had turned on.
     */
    private reporter: Reporter | undefined | null = null;

    /**
     * Report a fault, unless it is one we can already see from the server.
     *
     * `WidgetApiError` means the API answered and said no — a 429, a 503, a
     * refusal. Every one of those is already counted, rated and alarmed on
     * where it happened, and forwarding them here would drown the genuinely
     * new information in a copy of what we have.
     *
     * **Except from `place`, which is not our API at all.** That call goes to
     * the operator's own endpoint, so a refusal there is counted by nobody:
     * not by us, because the request never reached us, and not by this
     * reporter, because it wore the same error type as the ones we can see.
     * A bet that will not place is the last step of the only journey that
     * matters, and it was the one failure invisible from both ends. Found when
     * Oleg pressed Confirm on the live demo, saw nothing happen, and there was
     * no record anywhere to say whether it had failed.
     *
     * What survives the filter is the half we are otherwise blind to: a throw
     * out of our own code, and a request that never reached us at all. The
     * second is the noisier of the two — every bettor on a train produces one
     * — and it is kept anyway, because a page whose CSP blocks our bundle, or
     * a host an operator's network cannot resolve, looks exactly the same from
     * here and is the failure most likely to end an integration. Dedupe by
     * signature is what makes keeping it affordable: ten thousand offline
     * bettors are one row with a large number beside it.
     */
    private report(error: unknown, where: string): void {
      if (!worthReporting(error, where)) return;
      if (this.reporter === null) {
        this.reporter = createReporter({
          url: this.getAttribute('report-url')?.trim() ?? '',
          // From the browser surface rather than reached for globally, on the
          // same terms as `document` and every other capability here: a widget
          // that grabs a global is a widget a test cannot watch, and this is
          // the one path where being unable to watch it defeats the purpose.
          ...(browser.fetch === undefined ? {} : { fetch: browser.fetch }),
          ...(this.getAttribute('publishable-key')?.trim()
            ? { publishableKey: this.getAttribute('publishable-key')!.trim() }
            : {}),
        });
      }
      this.reporter?.(error, where);
    }

    private async refreshAccount(): Promise<void> {
      const url = this.getAttribute('account-url');
      if (url === null || url.trim() === '') return;

      const account = await this.ensureClient().fetchAccount(url.trim());
      this.update(reduce(this.state, { type: 'ACCOUNT', account }));
    }

    private async open(): Promise<void> {
      // A second click while the first is still in flight would mint a second
      // session against a per-origin budget that is shared with every other
      // visitor on the site.
      if (this.state.phase === 'OPENING') return;

      // The launcher is about to leave the screen. Nothing to animate, and a
      // repaint on a timer under an open panel is a cost paid for nothing.
      this.teaserRun += 1;

      // The next paint that actually draws a panel is its entrance; every one
      // after it is a repaint and must not replay the animation.
      this.panelEntering = true;

      this.update(reduce(this.state, { type: 'OPEN' }));

      this.locale = preferredLocale(
        this.getAttribute('locale'),
        browser.navigator?.language,
        'en',
      );

      let openingBundle: LocaleBundle | undefined;
      try {
        const client = this.ensureClient();
        /**
         * Strings first, session second.
         *
         * The other way round means a refused session leaves the panel with
         * nothing to render a refusal in, and the bettor gets a launcher that
         * does nothing when they press it. The strings need only the
         * publishable key, so they are available either way — and a demo link
         * that has spent its budget can say so in the language of the page.
         */
        const bundle = await client.localeBundle(this.locale);
        // Held so the catch below can render a refusal in the right language.
        openingBundle = bundle;
        await client.openSession();

        this.update(reduce(this.state, { type: 'OPENED', bundle }));
        this.dispatch(WIDGET_EVENTS.opened, { locale: bundle.resolved });
        // Not awaited: the panel opens on the bundle, and a balance that
        // arrives a moment later simply fills the card in. Waiting for a third
        // party's endpoint before showing anything would put their latency in
        // front of our own.
        void this.refreshAccount();
      } catch (error) {
        // A refused session is often ours to know about and never ours to see:
        // the request that would have told us is the one that just failed.
        this.report(error, 'open');
        this.update(
          reduce(this.state, {
            type: 'OPEN_FAILED',
            kind: errorKind(error),
            ...(openingBundle === undefined ? {} : { bundle: openingBundle }),
          }),
        );
        this.dispatch(WIDGET_EVENTS.failed, { kind: errorKind(error) });
      }
    }

    private close(): void {
      // Spent here rather than after the first paint. See panelEntering.
      this.panelEntering = false;
      this.update(reduce(this.state, { type: 'CLOSE' }));
      this.dispatch(WIDGET_EVENTS.closed);
      // Back to the pill, so the stage starts moving again.
      this.startTeaser();
    }

    disconnectedCallback(): void {
      // Otherwise the loop keeps repainting a detached tree for as long as the
      // page lives.
      this.teaserRun += 1;
    }

    private async ask(text: string, addTo?: string): Promise<void> {
      // Read before ASK appends this message, so it is exactly what was said
      // BEFORE the thing being answered — no slicing the new entry back off,
      // and no chance of the model being handed the question twice.
      const history = recentTurns(this.state.messages);

      this.focusInput = true;
      this.update(
        reduce(this.state, {
          type: 'ASK',
          text,
          at: clockTime(this.state.bundle?.resolved ?? this.locale, new Date()),
          // The bet stays on screen while the addition is fetched. See ASK.
          ...(addTo === undefined ? {} : { growing: true }),
        }),
      );

      try {
        const response = await this.ensureClient().parse(
          text,
          this.eventIds(),
          this.locale,
          history,
          addTo,
          this.state.context.oddsFormat,
        );
        this.focusInput = true;
        this.update(
          reduce(this.state, {
            type: 'ANSWERED',
            response,
            // See `growing`: an answer that found nothing must not take away
            // the slip the bettor was adding to.
            ...(addTo === undefined ? {} : { growing: true }),
          }),
        );
      } catch (error) {
        this.report(error, 'ask');
        this.update(reduce(this.state, { type: 'FAILED', kind: errorKind(error) }));
        this.dispatch(WIDGET_EVENTS.failed, { kind: errorKind(error) });
      }
    }

    /**
     * Add a selection to the slip on screen.
     *
     * Deliberately not routed through the thinking state the composer uses:
     * this is a press with a round trip behind it, not a question being
     * considered, and a "thinking" line under a slip that is already correct
     * would read as the assistant having second thoughts.
     */
    private async extend(slipId: string, refs: string[]): Promise<void> {
      try {
        const response = await this.ensureClient().extend(slipId, refs, this.readingLocale());
        if (!response.ok) {
          // The operator declined to price the pair. The slip they hold is
          // untouched and still confirmable, so this is a notice rather than a
          // wipe — see the reducer, which leaves the slip alone.
          this.update(reduce(this.state, { type: 'FAILED', kind: 'GENERIC' }));
          return;
        }
        this.update(
          reduce(this.state, {
            type: 'SLIP_EXTENDED',
            slip: response.slip,
            alternatives: [...response.alternatives],
          }),
        );
      } catch (error) {
        this.report(error, 'extend');
        this.update(reduce(this.state, { type: 'FAILED', kind: 'GENERIC' }));
      }
    }

    /**
     * Take a selection back off the slip on screen.
     *
     * Deliberately shaped like `extend`, including the refusal: a slip that
     * cannot be repriced without that leg — or one reduced to nothing — leaves
     * the bettor holding exactly what they had, with a notice rather than an
     * empty panel.
     */
    private async reduce(slipId: string, selectionIds: string[]): Promise<void> {
      try {
        const response = await this.ensureClient().reduce(slipId, selectionIds, this.readingLocale());
        if (!response.ok) {
          this.update(reduce(this.state, { type: 'FAILED', kind: 'GENERIC' }));
          return;
        }
        this.update(
          reduce(this.state, {
            type: 'SLIP_REDUCED',
            slip: response.slip,
            alternatives: [...response.alternatives],
          }),
        );
      } catch (error) {
        this.report(error, 'reduce');
        this.update(reduce(this.state, { type: 'FAILED', kind: 'GENERIC' }));
      }
    }

    private async confirm(slipId: string): Promise<void> {
      this.update(reduce(this.state, { type: 'CONFIRM' }));

      try {
        const response = await this.ensureClient().confirm(slipId);

        if (response.status === 'CONFIRMED') {
          this.update(reduce(this.state, { type: 'CONFIRMED' }));
          // The handoff. The operator's page takes this ticket to their own
          // betting engine, which is where a stake is collected and a bet is
          // actually placed. Nothing in this widget does either.
          this.dispatch(WIDGET_EVENTS.confirmed, {
            ticket: response.ticket,
            ticketId: response.ticketId,
            expiresAt: response.expiresAt,
          });
          return;
        }

        if (response.status === 'PRICE_MOVED') {
          this.update(
            reduce(this.state, {
              type: 'PRICE_MOVED',
              slip: response.slip,
              previousTotalPrice: response.previousTotalPrice,
            }),
          );
          return;
        }

        this.update(reduce(this.state, { type: 'FAILED', kind: 'GENERIC' }));
      } catch (error) {
        this.report(error, 'confirm');
        this.update(reduce(this.state, { type: 'FAILED', kind: errorKind(error) }));
        this.dispatch(WIDGET_EVENTS.failed, { kind: errorKind(error) });
      }
    }

    /**
     * Confirm the price with us, then place the bet with the operator.
     *
     * Two calls, in this order, and the order is the safety property. Ours
     * re-prices the slip against the live feed and refuses to sign anything
     * that moved — so a bet only ever reaches the operator's engine at a number
     * the bettor has already been shown. Placing first and checking after would
     * turn every ordinary market tick into a bet nobody agreed to.
     *
     * `PRICE_MOVED` therefore stops here with nothing placed, exactly as it
     * does for the handoff flow: the card comes back with the new price and the
     * button disabled until they agree again.
     */
    /**
     * Send a verdict, and never let it interrupt anything.
     *
     * Fire and forget, with the failure swallowed on purpose. The screen has
     * already acknowledged the tap; a bettor who said "that was wrong" and got
     * back "your opinion could not be saved" has been told about a problem
     * that is ours and that they cannot do anything about.
     */
    private sendFeedback(input: {
      verdict: 'HELPFUL' | 'NOT_HELPFUL' | 'MESSAGE';
      reason?: 'WRONG_MATCH' | 'WRONG_BET' | 'MISUNDERSTOOD';
      text?: string;
    }): void {
      const client = this.client;
      if (client === null) return;

      void client
        .feedback(
          {
            ...input,
            // The join. Absent on the standing form, which is about the
            // assistant rather than about any one answer.
            ...(this.state.answerId === null ? {} : { requestId: this.state.answerId }),
          },
          this.readingLocale(),
        )
        .catch(() => undefined);
    }

    private async place(slipId: string): Promise<void> {
      const stake = this.state.stakeAmount;
      const currency = this.state.stake?.account?.currency;
      const url = (this.getAttribute('place-bet-url') ?? '').trim();

      // No endpoint configured means this operator has not integrated in-chat
      // placement, so the bet takes the older road: a signed ticket handed to
      // their page. Falling back rather than failing is what keeps the feature
      // additive.
      if (url === '' || stake === null || currency === undefined) {
        await this.confirm(slipId);
        return;
      }

      // A second press while the first is in flight would confirm the slip
      // again — and a confirmed slip answers 409, so the bettor would be told
      // their own bet failed. Guarded here rather than by disabling the button
      // alone, because a repaint can race a very fast double tap.
      if (this.state.phase === 'PLACING' || this.state.phase === 'CONFIRMING') return;

      this.update(reduce(this.state, { type: 'PLACING' }));

      try {
        const confirmed = await this.ensureClient().confirm(slipId);

        if (confirmed.status === 'PRICE_MOVED') {
          this.update(
            reduce(this.state, {
              type: 'PRICE_MOVED',
              slip: confirmed.slip,
              previousTotalPrice: confirmed.previousTotalPrice,
            }),
          );
          return;
        }

        if (confirmed.status !== 'CONFIRMED') {
          this.update(reduce(this.state, { type: 'FAILED', kind: 'GENERIC' }));
          return;
        }

        const result = await this.ensureClient().placeBet(url, {
          ticket: confirmed.ticket,
          ticketId: confirmed.ticketId,
          stake,
          currency,
        });

        this.update(reduce(this.state, { type: 'PLACEMENT', result }));

        if (result.status === 'PLACED') {
          // The money has moved, so whatever is cached here is now wrong.
          // Re-asked rather than adjusted: the operator's own arithmetic is
          // the one that counts, and it knows about the other tab.
          void this.refreshAccount();
          this.dispatch(WIDGET_EVENTS.placed, {
            betId: result.betId,
            stake: result.stake,
            currency: result.currency,
            balance: result.balance,
          });
        }
      } catch (error) {
        // Reported ahead of the notice, and this is the call site that most
        // justifies the whole mechanism: a throw here happens with a stake
        // committed and a ticket possibly already issued. It is the one fault
        // an operator will be asked about by a bettor.
        this.report(error, 'place');
        // A ticket may well have been issued before this threw, which is why
        // the bettor is told it did not go through rather than told to try
        // again: the honest answer to "did my bet get placed" after a dropped
        // connection is that we cannot see, and the operator's own history can.
        this.update(reduce(this.state, { type: 'FAILED', kind: errorKind(error) }));
        this.dispatch(WIDGET_EVENTS.failed, { kind: errorKind(error) });
      }
    }
  }

  browser.customElements.define(WIDGET_TAG, BettingWidget);
}

/**
 * Register against the real page.
 *
 * Separate from `defineBettingWidget` so that importing this module in a test —
 * or on a server, where `window` does not exist — does nothing at all.
 */
export function registerBettingWidget(): void {
  const globals = globalThis as unknown as { customElements?: unknown; document?: unknown };
  if (globals.customElements === undefined || globals.document === undefined) return;

  defineBettingWidget(globalThis as unknown as WidgetBrowser);
}

/** Re-exported so a host page can build a spec-shaped node without the element. */
export { createNode };
